import { FC, useMemo } from 'react';

import { AccessLevelIcon } from '@dbt-labs/dbt-dag';
import {
  Link,
  RyeconErrorOutline,
  RyeconInfoOutline,
  Tooltip,
} from '@dbt-labs/sourdough';

import { AccessLevel, ModelQueryHistoryStatus } from '@/__generated__/graphql';
import { QUERY_USAGE_TOOLTIP_NO_DATA } from '@/components/Charts/ModelQueryUsageBarChart';
import { ColumnTable, Entry } from '@/components/ColumnTable';
import { RelationName } from '@/components/RelationName';
import ResourceOwner from '@/components/ResourceGroup';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useFeatureFlags } from '@/context/FeatureFlagProvider';
import { useModalProvider } from '@/context/ModalProvider';
import { useModel } from '@/hooks/dbtStrategy/useModel';
import { useLinkToDbtCloud, useLinkToExplorer } from '@/hooks/links';
import { useUrlProjectParams } from '@/hooks/params';
import { useCommonLineageEntries } from '@/hooks/useCommonLineageEntries';
import { modelQueryHistorySupported } from '@/util/dbt/adapter';
import { getNotEnabledMessage } from '@/util/queryHistoryModalMessage';
import { toTitleCase } from '@/util/string';
import { PanelDetails, ResourceDetailsParams } from './panelDetailTypes';
import { TagLinks } from './TagLinks';

const ErrorTooltip = () => (
  <div className="flex items-center space-x-1">
    <span className="text-fgDanger">Unknown Error</span>
    <Tooltip
      content={
        <>
          An error occurred and the data was not able to load. Learn how to troubleshoot
          permissions{' '}
          <Link
            isInternal={false}
            to="https://docs.getdbt.com/docs/collaborate/model-query-history#credential-permissions"
            onClick={(e) => e.stopPropagation()}
            shouldOpenNewTab
          >
            here
          </Link>
          .
        </>
      }
      placement="top-end"
    >
      <RyeconErrorOutline className="align-middle text-fgDanger" />
    </Tooltip>
  </div>
);

const PendingTooltip = () => (
  <div className="flex items-center space-x-1">
    <span className="text-fgInfo">Pending Data...</span>
    <Tooltip content={QUERY_USAGE_TOOLTIP_NO_DATA} placement="top-end">
      <RyeconInfoOutline className="align-middle text-fgInfo" />
    </Tooltip>
  </div>
);

const NudgeToolTip = () => {
  const { isAdmin, hasDeveloperLicense, environmentId } = useDbtCloudProject();
  const projectScope = useUrlProjectParams();
  const projectIdFromUrl = projectScope.projectId;
  const getEnvironmentLink = useLinkToDbtCloud('environmentDetailSettings');
  const { openModal } = useModalProvider();
  const link = getEnvironmentLink
    ? getEnvironmentLink({
        ...projectScope,
        projectId: projectIdFromUrl,
        environmentId: environmentId,
      })
    : undefined;
  const infoMessage = getNotEnabledMessage(isAdmin, hasDeveloperLicense, link);
  return (
    <>
      <span>
        {' '}
        <Link
          testId="usage-error-link"
          isInternal={true}
          onClick={() => {
            openModal(
              'Requires setup to view',
              'info',
              <span data-testid="info-message">{infoMessage}</span>,
            );
          }}
          to="#"
        >
          Requires setup
        </Link>{' '}
        to see data.
      </span>
    </>
  );
};

const getQueryHistoryDisplayData = (
  state?: ModelQueryHistoryStatus,
  modelQueryHistoryUsageCount?: number,
  queryHistoryAvailableNotEnabled?: boolean,
) => {
  if (queryHistoryAvailableNotEnabled) {
    return <NudgeToolTip />;
  }
  if (state === ModelQueryHistoryStatus.Success) {
    return modelQueryHistoryUsageCount?.toLocaleString();
  }
  if (state === ModelQueryHistoryStatus.Error) {
    return <ErrorTooltip />;
  }
  return <PendingTooltip />;
};

export const useModelDetailsPanel = ({
  uniqueId,
  projectName,
}: ResourceDetailsParams) => {
  const modelQuery = useModel(uniqueId);
  const projectScope = useUrlProjectParams();
  const { environmentType } = useUrlProjectParams();

  const { hasAccountFeature } = useDbtCloudAccount();
  const { enableModelQueryHistory } = useDbtCloudProject();
  const featureFlags = useFeatureFlags();

  // customer has access to query history but is not enabled
  const queryHistoryAvailableNotEnabled =
    hasAccountFeature('explorerQueryHistory') &&
    !enableModelQueryHistory &&
    modelQueryHistorySupported(
      modelQuery.data?.adapterType || '',
      environmentType,
      featureFlags,
    );

  const getGroupLink = useLinkToExplorer('resourceDetails');
  const commonRows = useCommonLineageEntries(projectName);

  return useMemo(() => {
    const model = modelQuery.data.applied;
    if (!model) {
      return { ...modelQuery, rows: null, description: null } satisfies PanelDetails;
    }
    const modelQueryHistoryUsageCount =
      'usageQueryCount' in model ? model.usageQueryCount : undefined;
    const modelQueryHistoryState = modelQuery.data.modelQueryHistoryState?.state;

    const rows: Entry[] = [
      ...commonRows,
      {
        key: 'Owner',
        data: (
          <ResourceOwner
            owner={model.group}
            href={getGroupLink({
              ...projectScope,
              dbtUniqueId: `group.${model.packageName}.${model.group}`,
            })}
            ctaHref="https://docs.getdbt.com/docs/build/groups"
          />
        ),
      },
      {
        key: 'tags',
        data: <TagLinks tags={model.tags} />,
      },
      ...(enableModelQueryHistory || queryHistoryAvailableNotEnabled
        ? [
            {
              key: 'Consumption queries (excludes builds)',
              data: getQueryHistoryDisplayData(
                modelQueryHistoryState,
                modelQueryHistoryUsageCount
                  ? (modelQueryHistoryUsageCount as number)
                  : undefined,
                queryHistoryAvailableNotEnabled,
              ),
            },
          ]
        : []),
      {
        key: 'Relation',
        data: <RelationName relation={model} />,
        disableTooltip: true,
      },
      {
        key: 'Language',
        data: model.language,
      },
      ...(model.access
        ? [
            {
              key: 'Model access (mesh)',
              data: <ModelAccessEntry access={model.access as AccessLevel} />,
            },
          ]
        : []),
      ...(model.contractEnforced != null
        ? [
            {
              key: 'Contract enforced',
              data: model.contractEnforced ? 'true' : 'false',
            },
          ]
        : []),
      {
        key: 'Package',
        data: uniqueId.packageName,
      },
      {
        key: 'Materialization Type', // consistent naming with lens
        data: model.materializedType,
      },
    ];

    return {
      ...modelQuery,
      rows,
      description: model.description,
    } satisfies PanelDetails;
  }, [
    modelQuery,
    commonRows,
    getGroupLink,
    projectScope,
    enableModelQueryHistory,
    queryHistoryAvailableNotEnabled,
    uniqueId.packageName,
  ]);
};

export const ModelDetailsTable: FC<ResourceDetailsParams> = ({
  className,
  children,
  compact,
  ...props
}) => {
  const modelDetails = useModelDetailsPanel(props);

  return (
    <div className={className}>
      {children?.(modelDetails)}
      <ColumnTable
        tableEntries={modelDetails.rows}
        compact={compact}
        isLoading={modelDetails.fetching}
      />
    </div>
  );
};

interface ModelAccessEntryProps {
  access: AccessLevel;
}

const ModelAccessEntry: FC<ModelAccessEntryProps> = ({ access }) => {
  return (
    <span className="flex items-center gap-2">
      <AccessLevelIcon
        size="sm"
        access={access}
        tooltipConfig={{ hidden: true }}
        iconClassName="h-4 w-4 text-fgMain"
      />
      {toTitleCase(access)}
    </span>
  );
};
